import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { MonteCarloResult } from '../utils/financialCalculations';

const NUM_BINS = 30;

export default function MonteCarloChart({ monteCarlo, printMode }: { monteCarlo: MonteCarloResult; printMode?: boolean }) {
  const { bins, stats } = useMemo(() => {
    const { npvs, mean, p10, p50, p90 } = monteCarlo;
    const positivePct = (npvs.filter(v => v > 0).length / npvs.length) * 100;
    const minNpv = npvs[0], maxNpv = npvs[npvs.length - 1], range = maxNpv - minNpv || 1, binWidth = range / NUM_BINS;
    const binData = Array.from({ length: NUM_BINS }, (_, b) => {
      const lo = minNpv + b * binWidth, hi = lo + binWidth, center = (lo + hi) / 2;
      return { binLabel: `€${(center / 1e6).toFixed(1)}M`, binCenter: center, count: npvs.filter(v => v >= lo && (b === NUM_BINS - 1 ? v <= hi : v < hi)).length };
    });
    return { bins: binData, stats: { mean, p10, p50, p90, positivePct } };
  }, [monteCarlo]);

  const fmt = (v: number) => `€${(v / 1e6).toFixed(2)}M`;
  return (
    <div className="bg-white rounded-xl border border-[#D9E2EC] shadow-sm p-4 mt-4">
      <div className="flex justify-between items-start mb-3">
        <div><div className="text-xs font-bold text-[#0B1F4A]">🎲 Monte Carlo Simulatie</div><div className="text-[10px] text-[#8899AA]">NPV-verdeling ({monteCarlo.npvs.length} iteraties)</div></div>
      </div>
      <div className="grid grid-cols-5 gap-2 mb-3">
        {[{ label: 'Gemiddelde NPV', value: fmt(stats.mean), color: stats.mean >= 0 ? '#10B981' : '#EF4444' },
          { label: 'P10', value: fmt(stats.p10), color: stats.p10 >= 0 ? '#10B981' : '#EF4444' },
          { label: 'P50', value: fmt(stats.p50), color: stats.p50 >= 0 ? '#10B981' : '#EF4444' },
          { label: 'P90', value: fmt(stats.p90), color: '#10B981' },
          { label: 'Kans NPV > 0', value: `${stats.positivePct.toFixed(0)}%`, color: stats.positivePct >= 50 ? '#10B981' : '#EF4444' },
        ].map((s, i) => <div key={i} className="bg-[#F5F8FB] rounded-lg p-2 text-center"><div className="text-[9px] text-[#8899AA]">{s.label}</div><div className="text-sm font-bold" style={{ color: s.color }}>{s.value}</div></div>)}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={bins} margin={{ top: 10, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="binLabel" tick={{ fontSize: 9, fill: '#8899AA' }} interval={Math.floor(NUM_BINS / 6)} />
          <YAxis tick={{ fontSize: 10, fill: '#8899AA' }} />
          <Tooltip formatter={(value: number) => [value, 'Iteraties']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={!printMode}>{bins.map((entry, i) => <Cell key={i} fill={entry.binCenter >= 0 ? '#10B981' : '#EF4444'} fillOpacity={0.8} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
